/**
 * Reusable face capture view. Shows live camera feed with an overlay ring,
 * loads face-api.js models lazily, and exposes a `capture()` API that returns
 * { descriptor, photoDataUrl } or { error }.
 *
 * Props:
 *   facingMode    'user' | 'environment'   (default 'user')
 *   onCapture     (result) => void          required
 *   busy          boolean — disables capture button during network call
 *   captureLabel  string                    default 'Capture'
 *   helpText      string                    default 'Center your face in the ring'
 */
import React, { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, AlertCircle, RefreshCw, Timer } from 'lucide-react';
import { loadFaceModels, captureFaceDescriptor, startVideoStream, stopVideoStream, isModelsLoaded, computeHeadYaw, classifyPose, captureFaceWithLiveness, randomLivenessChallenge } from '../lib/faceRecognition';

export default function FaceCaptureView({
  facingMode: initialFacing = 'user',
  onCapture,
  busy = false,
  captureLabel = 'Capture',
  helpText = 'Look at the camera — capturing automatically…',
  allowCameraSwitch = true,
  requiredPose = null,   // 'front' | 'left' | 'right' | null (any pose ok)
  liveness = false,      // when true, runs an active anti-spoofing challenge before capture
  forEnrollment = false, // stricter quality gates (face size, confidence, lighting)
}) {
  // Deliberate: no gallery/file input. Capture MUST come from the live camera
  // stream (videoEl.srcObject MediaStream). See anti-spoofing requirements.
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState(initialFacing);
  const [modelStatus, setModelStatus] = useState(isModelsLoaded() ? 'ready' : 'loading');
  const [camStatus, setCamStatus] = useState('starting');
  const [working, setWorking] = useState(false);
  const [hint, setHint] = useState(isModelsLoaded() ? helpText : 'Warming up face engine…');
  const [livePose, setLivePose] = useState(null); // 'front' | 'left' | 'right' | 'transition' | null
  const [poseStable, setPoseStable] = useState(0);   // consecutive frames matching requiredPose (0..STABLE_FRAMES)
  const [challengePrompt, setChallengePrompt] = useState(null); // liveness head-turn prompt
  const [captureMs, setCaptureMs] = useState(null);   // live elapsed time of the active capture
  const timerRef = useRef(null);
  // Synchronous guard — set BEFORE any async work so a second call (auto-capture
  // racing the manual button) returns immediately without waiting for React to
  // re-render and disable the button. This prevents double-punches.
  const workingRef = useRef(false);

  // Clear any running capture timer on unmount
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await loadFaceModels();
        if (!active) return;
        setModelStatus('ready');
      } catch (e) {
        if (!active) return;
        setModelStatus('error');
        setHint('Failed to load face recognition models. Check your internet connection.');
      }
    })();
    return () => { active = false; };
  }, []);

  // Restart camera whenever facing mode changes
  useEffect(() => {
    let active = true;
    setCamStatus('starting');
    (async () => {
      stopVideoStream(streamRef.current);
      try {
        const stream = await startVideoStream(videoRef.current, facingMode);
        if (!active) { stopVideoStream(stream); return; }
        streamRef.current = stream;
        setCamStatus('on');
      } catch (e) {
        if (!active) return;
        setCamStatus('error');
        setHint('Camera permission denied. Allow camera access and refresh.');
      }
    })();
    return () => {
      active = false;
      stopVideoStream(streamRef.current);
    };
  }, [facingMode]);

  // Optional auto-capture: when models+camera are ready, poll for a face and
  // (when `requiredPose` is set) verify head orientation. Capture only after
  // the user holds the correct pose for STABLE_FRAMES consecutive ticks.
  useEffect(() => {
    if (modelStatus !== 'ready' || camStatus !== 'on' || working || busy) return;
    let cancelled = false;
    let stableCount = 0;
    const STABLE_FRAMES = requiredPose ? 3 : 2;     // ~0.9s of correct pose
    const tick = async () => {
      if (cancelled || !videoRef.current) return;
      try {
        const faceapi = await import('face-api.js');
        // Liveness path (kiosk anti-spoofing): wait for a stable face, then run
        // the active challenge sequence instead of a plain presence capture.
        if (liveness) {
          const det = await faceapi.detectSingleFace(
            videoRef.current,
            new faceapi.TinyFaceDetectorOptions({ inputSize: 160, scoreThreshold: 0.5 }),
          );
          if (cancelled) return;
          if (det && det.score >= 0.6) {
            stableCount += 1;
            if (stableCount >= 2) { cancelled = true; runLiveness(); return; }
          } else {
            stableCount = 0;
          }
          setTimeout(tick, 300);
          return;
        }
        // Pose-aware path: detect with landmarks so we can compute yaw
        if (requiredPose) {
          const det = await faceapi
            .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 }))
            .withFaceLandmarks();
          if (cancelled) return;
          if (det && det.detection.score >= 0.55) {
            const yaw = computeHeadYaw(det.landmarks);
            const pose = classifyPose(yaw);
            setLivePose(pose);
            if (pose === requiredPose) {
              stableCount += 1;
              setPoseStable(stableCount);
              if (stableCount >= STABLE_FRAMES) {
                cancelled = true;
                capture();
                return;
              }
            } else {
              stableCount = Math.max(0, stableCount - 1);
              setPoseStable(stableCount);
            }
          } else {
            stableCount = 0;
            setPoseStable(0);
            setLivePose(null);
          }
        } else {
          // Legacy fast path — no pose check, just detect presence
          const det = await faceapi.detectSingleFace(
            videoRef.current,
            new faceapi.TinyFaceDetectorOptions({ inputSize: 160, scoreThreshold: 0.5 }),
          );
          if (cancelled) return;
          if (det && det.score >= 0.6) {
            stableCount += 1;
            if (stableCount >= STABLE_FRAMES) {
              cancelled = true;
              capture();
              return;
            }
          } else {
            stableCount = 0;
          }
        }
      } catch { /* ignore — fall back to manual capture */ }
      setTimeout(tick, 300);
    };
    const t = setTimeout(tick, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelStatus, camStatus, busy, requiredPose, liveness]);

  // Active liveness challenge: pick a (sometimes random head-turn) challenge,
  // prompt naturally, capture signals, and silently retry on an obvious fail so
  // the worker is never told a liveness test is running. The SERVER decides.
  const runLiveness = async () => {
    if (workingRef.current || modelStatus !== 'ready' || camStatus !== 'on') return;
    workingRef.current = true;
    setWorking(true);
    const _t0 = performance.now();
    setCaptureMs(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCaptureMs(performance.now() - _t0), 100);
    const MAX_TRIES = 3;
    let result = null;
    try {
      for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
        const challenge = randomLivenessChallenge();
        setChallengePrompt(challenge || 'front');
        setHint(
          challenge === 'left' ? 'Please turn slightly left'
            : challenge === 'right' ? 'Please turn slightly right'
              : 'Please face the camera directly',
        );
        await new Promise((r) => setTimeout(r, 300));   // brief beat so the user sees the prompt and starts turning
        setHint('Detecting…');
        const res = await captureFaceWithLiveness(videoRef.current, { challenge });
        if (res.error) { setHint(res.error); await new Promise((r) => setTimeout(r, 500)); continue; }
        result = res;
        const lv = res.liveness;
        const motionLow = (lv.motion_variance ?? 0) < 1.5;
        const challengeFail = lv.challenge_passed === false;
        if ((motionLow || challengeFail) && attempt < MAX_TRIES - 1) {
          // Misdirection — never reveal the anti-spoofing check.
          setHint('Please hold still and look at the camera');
          await new Promise((r) => setTimeout(r, 600));
          continue;
        }
        break;
      }
    } catch {
      setHint('Capture failed. Try again.');
    }
    setChallengePrompt(null);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const _dur = Math.round(performance.now() - _t0);
    setCaptureMs(null);
    workingRef.current = false;
    if (result && !result.error) {
      setHint(`Face captured ✓ · ${(_dur / 1000).toFixed(1)}s`);
      onCapture({ ...result, captureMs: _dur });
    } else {
      setHint('Please rescan.');
    }
    setWorking(false);
  };

  const capture = async () => {
    if (workingRef.current || modelStatus !== 'ready' || camStatus !== 'on') return;
    workingRef.current = true;
    setWorking(true);
    const _t0 = performance.now();
    setCaptureMs(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCaptureMs(performance.now() - _t0), 100);
    try {
      const result = await captureFaceDescriptor(videoRef.current);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      const _dur = Math.round(performance.now() - _t0);
      setCaptureMs(null);
      if (result.error) {
        setHint(result.error);
      } else {
        setHint(`Face captured ✓ · ${(_dur / 1000).toFixed(1)}s`);
        onCapture({ ...result, captureMs: _dur });
      }
    } catch (e) {
      setHint('Capture failed. Try again.');
    } finally {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      setCaptureMs(null);
      workingRef.current = false;
      setWorking(false);
    }
  };

  const ready = modelStatus === 'ready' && camStatus === 'on' && !working && !busy;

  return (
    <div className="w-full max-w-md mx-auto" data-testid="face-capture-view">
      <div className="relative aspect-square bg-black rounded-2xl overflow-hidden shadow-2xl">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover transform"
          style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
          data-testid="face-capture-video"
        />
        {/* Centered loading state — far more visible than a small top badge */}
        {(modelStatus === 'loading' || camStatus === 'starting') && (
          <div className="absolute inset-0 bg-slate-900/80 backdrop-blur flex flex-col items-center justify-center text-white" data-testid="face-capture-loading">
            <div className="relative">
              <div className="w-20 h-20 rounded-full border-4 border-white/20 border-t-white animate-spin" />
              <Camera size={28} className="absolute inset-0 m-auto text-white/80" />
            </div>
            <div className="mt-5 font-display font-bold text-sm uppercase tracking-widest">
              {modelStatus === 'loading' ? 'Loading face engine…' : 'Starting camera…'}
            </div>
            <div className="text-[11px] text-white/50 mt-1">
              {modelStatus === 'loading' ? 'One-time setup · ~7 MB' : 'Allow camera permission if prompted'}
            </div>
          </div>
        )}
        {/* Reticle overlay (only when ready) */}
        {modelStatus === 'ready' && camStatus === 'on' && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className={`w-3/4 aspect-square rounded-full border-2 ${working ? 'border-emerald-400 animate-pulse' : 'border-white/40'} shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]`} />
          </div>
        )}
        {/* Live capture timer — visible feedback on how fast detection is */}
        {working && captureMs != null && (
          <div className="absolute top-3 left-3 bg-black/60 backdrop-blur text-white rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs font-display font-bold tabular-nums" data-testid="face-capture-timer">
            <Timer size={13} className="text-emerald-300" /> {(captureMs / 1000).toFixed(1)}s
          </div>
        )}
        {/* Pose coaching banner — only when requiredPose is set */}
        {requiredPose && modelStatus === 'ready' && camStatus === 'on' && !working && (
          <div className="absolute bottom-3 left-3 right-3" data-testid="pose-coaching">
            {livePose === requiredPose ? (
              <div className="bg-emerald-500/95 backdrop-blur text-white px-4 py-2 rounded-full text-xs font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2">
                ✓ Holding {requiredPose} pose · {Math.min(poseStable, 4)}/4
                <div className="flex gap-1 ml-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className={`w-1.5 h-1.5 rounded-full ${i < poseStable ? 'bg-white' : 'bg-white/30'}`} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-amber-500/95 backdrop-blur text-slate-900 px-4 py-2 rounded-full text-xs font-display font-bold uppercase tracking-wider text-center">
                {requiredPose === 'front' && (livePose === 'left' || livePose === 'right')
                  ? '← Center your face — look straight at camera'
                  : requiredPose === 'left'
                  ? (livePose === 'right' ? '⤺ Turn your head to the LEFT' : livePose === 'front' ? '⤺ Turn LEFT more (about 30°)' : '⤺ Turn your head to the LEFT')
                  : requiredPose === 'right'
                  ? (livePose === 'left' ? '⤻ Turn your head to the RIGHT' : livePose === 'front' ? '⤻ Turn RIGHT more (about 30°)' : '⤻ Turn your head to the RIGHT')
                  : 'Position your face inside the ring'}
              </div>
            )}
          </div>
        )}
        {/* Liveness challenge prompt — natural guidance, never "liveness test" */}
        {liveness && challengePrompt && modelStatus === 'ready' && camStatus === 'on' && (
          <div className="absolute bottom-3 left-3 right-3" data-testid="liveness-prompt">
            <div className="bg-white/95 backdrop-blur text-slate-900 px-4 py-2.5 rounded-full text-sm font-display font-bold text-center shadow-lg">
              {challengePrompt === 'left' ? '⤺ Please turn slightly LEFT'
                : challengePrompt === 'right' ? '⤻ Please turn slightly RIGHT'
                  : 'Please face the camera directly'}
            </div>
          </div>
        )}
        {/* Camera switch */}
        {allowCameraSwitch && modelStatus === 'ready' && (
          <button
            type="button"
            onClick={() => setFacingMode((m) => (m === 'user' ? 'environment' : 'user'))}
            className="absolute top-3 right-3 bg-black/60 backdrop-blur text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/80"
            aria-label="Switch camera"
            data-testid="face-capture-switch-cam"
          >
            <RefreshCw size={16} />
          </button>
        )}
        {(modelStatus === 'error' || camStatus === 'error') && (
          <div className="absolute top-3 left-3 right-3 bg-rose-600/90 backdrop-blur px-3 py-2 rounded-full text-xs text-white flex items-center gap-2">
            <AlertCircle size={14} /> {hint}
          </div>
        )}
      </div>
      <p className="mt-3 text-center text-xs text-slate-300">{hint}</p>
      <button
        type="button"
        onClick={liveness ? runLiveness : capture}
        disabled={!ready}
        className="mt-4 w-full bg-white text-slate-900 font-display font-bold py-4 rounded-full flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg"
        data-testid="face-capture-btn"
      >
        {working || busy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
        {working ? 'Detecting…' : busy ? 'Sending…' : captureLabel}
      </button>
    </div>
  );
}
